/**
 * Tab3SurgicalPreview.jsx v1.0.0 - 2026-10-18 (Tab 3 review, Dr. Tatar: live preview of the surgical load)
 *
 * One run of the calculator with the Tab 3 settings on screen (nothing is saved) and, per day for the selected brigade: the wounded,
 * the surgical cases, the backlog carried in, the capacity of the day, the operations tempo (normal / surge / crisis) with the
 * average surgeon performance, the backlog left and the died of wounds. The tempo follows the load; the policy of the planner caps it.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper
} from '@mui/material';
import { calculateCasualties } from '../calculationEngine/casualtyCalculator';

const TEMPO_COLOR = { NORMAL: 'success', SURGE: 'warning', CRISIS: 'error' };
const r1 = (n) => (typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 10) / 10 : '-');

const Tab3SurgicalPreview = ({ getScenario }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ status: 'idle', brigade: 0 });

  const run = () => {
    try {
      const result = calculateCasualties(getScenario(), { final: false });
      setState({ status: 'done', result, brigade: 0 });
    } catch (e) {
      setState({ status: 'error', message: e.message, brigade: 0 });
    }
  };

  const brigades = state.status === 'done' ? (state.result.by_brigade || []) : [];
  const b = brigades[state.brigade] || null;
  const rows = b ? b.by_day.map((d) => {
    const tp = d.resources && d.resources.surgical_tempo;
    return {
      day: d.day,
      wia: d.battle ? d.battle.wia : null,
      tempo: tp ? tp.tempo : null,
      cases: tp ? tp.demand_cases : null,
      routed: d.resources && d.resources.surgical_regulating ? d.resources.surgical_regulating.routed_total : 0,
      backlogIn: tp ? tp.backlog_in : null,
      capacity: tp ? tp.capacity_cases : null,
      perf: tp ? tp.performance : null,
      load: tp ? tp.load_ratio : null,
      backlogOut: d.resources ? d.resources.surgical_backlog : null,
      dow: d.battle ? d.battle.dow : null,
      beyond: tp ? tp.beyond_sustainable_days : false
    };
  }) : [];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t('tab3.preview_title')}</Typography>
        <Button variant="outlined" onClick={run}>{t('tab3.preview_run')}</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('tab3.preview_help')}</Typography>
      {state.status === 'error' && <Alert severity="error" sx={{ mt: 1 }}>{t('tab3.preview_error')}: {state.message}</Alert>}
      {state.status === 'done' && (
        <>
          {brigades.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 220, my: 1 }}>
              <InputLabel>{t('tab3.preview_brigade')}</InputLabel>
              <Select value={state.brigade} label={t('tab3.preview_brigade')} onChange={(e) => setState({ ...state, brigade: e.target.value })}>
                {brigades.map((x, i) => <MenuItem key={x.brigade_id || i} value={i}>{x.brigade_name || `${t('tab3.preview_brigade')} ${i + 1}`}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tab3.preview_day')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_wia')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_cases')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_routed')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_backlog_in')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_capacity')}</TableCell>
                  <TableCell>{t('tab3.preview_tempo')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_performance')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_backlog_out')}</TableCell>
                  <TableCell align="right">{t('tab3.preview_dow')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.day}>
                    <TableCell>{r.day}</TableCell>
                    <TableCell align="right">{r.wia}</TableCell>
                    <TableCell align="right">{r1(r.cases)}</TableCell>
                    <TableCell align="right">{r1(r.routed)}</TableCell>
                    <TableCell align="right">{r1(r.backlogIn)}</TableCell>
                    <TableCell align="right">{r1(r.capacity)}</TableCell>
                    <TableCell>
                      {r.tempo
                        ? <Chip size="small" color={TEMPO_COLOR[r.tempo]} label={`${t('tab3.tempo_' + r.tempo.toLowerCase())}${r.beyond ? ' !' : ''}`} />
                        : '-'}
                    </TableCell>
                    <TableCell align="right">{r.perf === null ? '-' : `${Math.round(r.perf * 100)}%`}</TableCell>
                    <TableCell align="right">{r1(r.backlogOut)}</TableCell>
                    <TableCell align="right">{r.dow}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{t('tab3.preview_legend')}</Typography>
        </>
      )}
    </Box>
  );
};

export default Tab3SurgicalPreview;
